import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

/**
 * Fila del listado de facturas de almacén.
 *
 * Los primeros campos son exactamente los que devuelve `GET /api/invoice/list`
 * (ver InvoiceController@index), para que el grid del panel compartido no tenga
 * que distinguir de dónde vienen los datos. Los últimos son los propios de
 * almacén, que hoy no existen en la BD.
 */
export interface WarehouseInvoiceRow {
    id: number;
    folio: string;
    total: number;
    issued_at: string;
    status: 'Vigente' | 'Pagada' | 'Cancelada';
    payment_status: 'Pagado' | 'Pendiente' | 'Vencido';
    payment_method: 'PUE' | 'PPD';
    client_id: number;
    client_name: string;
    client_rfc: string;
    is_refactura: boolean;
    was_refacturada: boolean;

    // --- Campos CFDI que el listado real no expone pero que aquí se generan
    // completos para poder validar la coherencia de la data de prueba. ---
    serie: string;
    uuid_sat: string;
    /** Vencimiento = emisión + CREDIT_DAYS. Es lo que decide Pendiente/Vencido. */
    due_at: string;
    subtotal: number;
    iva: number;
    /** c_FormaPago del SAT. PPD obliga a "99 - Por definir". */
    payment_form_key: string;
    product_service_key: string;
    unit_key: string;
    quantity: number;
    unit_price: number;

    // --- Propios de almacén ---
    /** Almacén al que pertenece la factura; casa con WarehouseService.warehouses. */
    warehouse: string;
    /** Servicio de almacén facturado; casa con el desglose por insumo de KPI's. */
    concept: string;
}

/** Un tramo de antigüedad por cliente, más la fila de totales. */
export interface WarehouseReceivableBuckets {
    /** Aún no vence. */
    current: number;
    d1_30: number;
    d31_60: number;
    d61_90: number;
    d91_120: number;
    d120_plus: number;
    total: number;
}

export interface WarehouseReceivableRow extends WarehouseReceivableBuckets {
    client_id: number;
    client_name: string;
}

export interface WarehouseReceivable {
    totals: WarehouseReceivableBuckets;
    rows: WarehouseReceivableRow[];
}

/** Lo facturado de un concepto de almacén. */
export interface WarehouseChargedConcept {
    concept: string;
    charged: number;
}

export interface WarehouseChargedMonth {
    /** `aaaa-mm`. */
    month: string;
    /** Etiqueta corta del eje del gráfico, p. ej. `Jul`. */
    label: string;
    charged: number;
    byConcept: WarehouseChargedConcept[];
}

/**
 * Lo facturado, agregado por concepto y por mes. Es de aquí de donde salen los
 * KPI's de almacén: mientras las tarjetas, el desglose y el gráfico se lean de
 * este mismo resumen no pueden desviarse entre sí ni del listado de facturas.
 */
export interface WarehouseChargedSummary {
    total: number;
    byConcept: WarehouseChargedConcept[];
    byMonth: WarehouseChargedMonth[];
}

/** Cliente de almacén con los datos fiscales que pide el CFDI 4.0. */
export interface WarehouseClient {
    id: number;
    name: string;
    rfc: string;
    /** Razón social tal cual está en la Constancia de Situación Fiscal. */
    businessName: string;
    fiscalRegimeKey: string;
    fiscalRegimeName: string;
    /** Código postal del domicilio fiscal: es el que va en el CFDI. */
    postalCode: string;
    address: string;
    email: string;
    cfdiUseKey: string;
    cfdiUseName: string;
}

/** Servicio de almacén prestado y todavía sin facturar. */
export interface WarehousePendingService {
    id: number;
    folio: string;
    clientId: number;
    clientName: string;
    warehouse: string;
    concept: string;
    productServiceKey: string;
    unitKey: string;
    quantity: number;
    unitPrice: number;
    /** `aaaa-mm-dd` en que se prestó el servicio. */
    servedAt: string;
}

/** Lo que la pantalla de alta manda para timbrar. */
export interface WarehouseInvoiceDraft {
    clientId: number;
    warehouse: string;
    concept: string;
    productServiceKey: string;
    quantity: number;
    unitPrice: number;
    paymentMethod: 'PUE' | 'PPD';
    paymentFormKey: string;
}

export interface WarehouseInvoiceFilters {
    search?: string;
    issued_at?: string;
    status?: string;
    payment_status?: string;
    payment_method?: string;
    warehouse?: string;
}

/** Días de crédito con los que se calcula el vencimiento (los mismos que usa
 *  por defecto `invoice/accounts-receivable`). */
const CREDIT_DAYS = 30;
const IVA_RATE = 0.16;

/**
 * Servicios de almacén facturables. Los nombres son los mismos que el desglose
 * por insumo de la pestaña de KPI's: si se editan allí, hay que editarlos aquí
 * o las dos pestañas dejan de hablar del mismo catálogo.
 *
 * Las claves SAT son las plausibles para cada concepto (almacenaje para los
 * servicios, materiales para los insumos, autotransporte para las guías).
 */
const CONCEPTS: Array<{ name: string; productServiceKey: string; unitKey: string }> = [
    { name: 'Preparación paquetería', productServiceKey: '78131800', unitKey: 'E48' },
    { name: 'Preparación paquetería palets', productServiceKey: '78131800', unitKey: 'E48' },
    { name: 'Preparación de pedidos fuera de horario (cargas, descargas, maniobras)', productServiceKey: '78131800', unitKey: 'ACT' },
    { name: 'Insumos (fleje, carton, esquineros...)', productServiceKey: '31201500', unitKey: 'H87' },
    { name: 'Tarimas para salidas', productServiceKey: '78131800', unitKey: 'H87' },
    { name: 'Playos', productServiceKey: '31201500', unitKey: 'H87' },
    { name: 'Papeleria', productServiceKey: '14111500', unitKey: 'H87' },
    { name: 'Paquetería guias', productServiceKey: '78101800', unitKey: 'E48' }
];

/** Clientes ficticios. Los RFC cumplen el formato de persona moral que exige
 *  el SAT: 3 letras + fecha AAMMDD válida + 3 de homoclave. */
const CLIENTS: WarehouseClient[] = [
    {
        id: 101, name: 'Comercializadora del Norte S.A. de C.V.', rfc: 'CNO120315J38',
        businessName: 'COMERCIALIZADORA DEL NORTE SA DE CV',
        fiscalRegimeKey: '601', fiscalRegimeName: 'General de Ley Personas Morales',
        postalCode: '66600', address: 'Carr. Miguel Alemán Km 24, Parque Industrial, Apodaca, Nuevo León',
        email: 'facturacion@comernorte.mx', cfdiUseKey: 'G03', cfdiUseName: 'Gastos en general'
    },
    {
        id: 102, name: 'Distribuidora Regia S. de R.L. de C.V.', rfc: 'DRE150722K51',
        businessName: 'DISTRIBUIDORA REGIA S DE RL DE CV',
        fiscalRegimeKey: '601', fiscalRegimeName: 'General de Ley Personas Morales',
        postalCode: '64000', address: 'Av. Constitución 1250 Ote., Centro, Monterrey, Nuevo León',
        email: 'cuentas@distribuidoraregia.mx', cfdiUseKey: 'G03', cfdiUseName: 'Gastos en general'
    },
    {
        id: 103, name: 'Industrias Apodaca S.A. de C.V.', rfc: 'IAP090218H27',
        businessName: 'INDUSTRIAS APODACA SA DE CV',
        fiscalRegimeKey: '601', fiscalRegimeName: 'General de Ley Personas Morales',
        postalCode: '66600', address: 'Av. Industrias 405, Parque Industrial Apodaca, Apodaca, Nuevo León',
        email: 'pagos@industriasapodaca.com', cfdiUseKey: 'G01', cfdiUseName: 'Adquisición de mercancías'
    },
    {
        id: 104, name: 'Grupo Textil Monterrey S.A. de C.V.', rfc: 'GTM110604Q19',
        businessName: 'GRUPO TEXTIL MONTERREY SA DE CV',
        fiscalRegimeKey: '601', fiscalRegimeName: 'General de Ley Personas Morales',
        postalCode: '64988', address: 'Av. Eloy Cavazos 3410, La Silla, Guadalupe, Nuevo León',
        email: 'administracion@gtextilmty.mx', cfdiUseKey: 'G03', cfdiUseName: 'Gastos en general'
    },
    {
        id: 105, name: 'Alimentos del Bajío S.A. de C.V.', rfc: 'ABA170912M44',
        businessName: 'ALIMENTOS DEL BAJIO SA DE CV',
        fiscalRegimeKey: '626', fiscalRegimeName: 'Régimen Simplificado de Confianza',
        postalCode: '67100', address: 'Carr. Miguel Alemán Km 18, Zona Industrial, Guadalupe, Nuevo León',
        email: 'facturas@alimentosbajio.mx', cfdiUseKey: 'G01', cfdiUseName: 'Adquisición de mercancías'
    },
    {
        id: 106, name: 'Electrónica Santa Catarina S.A. de C.V.', rfc: 'ESC131128R60',
        businessName: 'ELECTRONICA SANTA CATARINA SA DE CV',
        fiscalRegimeKey: '601', fiscalRegimeName: 'General de Ley Personas Morales',
        postalCode: '66350', address: 'Av. Manuel Ordóñez 820, Santa Catarina, Nuevo León',
        email: 'cxp@electronicasc.com.mx', cfdiUseKey: 'G03', cfdiUseName: 'Gastos en general'
    },
    {
        id: 107, name: 'Plásticos Guadalupe S. de R.L. de C.V.', rfc: 'PGU080425B82',
        businessName: 'PLASTICOS GUADALUPE S DE RL DE CV',
        fiscalRegimeKey: '626', fiscalRegimeName: 'Régimen Simplificado de Confianza',
        postalCode: '67190', address: 'Av. Pablo Livas 5500, Valle de Guadalupe, Guadalupe, Nuevo León',
        email: 'facturacion@plasticosgpe.mx', cfdiUseKey: 'G01', cfdiUseName: 'Adquisición de mercancías'
    },
    {
        id: 108, name: 'Refacciones García S.A. de C.V.', rfc: 'RGA190301D73',
        businessName: 'REFACCIONES GARCIA SA DE CV',
        fiscalRegimeKey: '601', fiscalRegimeName: 'General de Ley Personas Morales',
        postalCode: '66000', address: 'Carr. a Villa de García Km 3.5, García, Nuevo León',
        email: 'compras@refaccionesgarcia.mx', cfdiUseKey: 'G03', cfdiUseName: 'Gastos en general'
    }
];

const WAREHOUSES = ['Almacén 109', 'Monterrey', 'Almacén 110'];

/**
 * Semilla de cada factura. `days` son días hacia atrás desde hoy, no fechas
 * fijas: así el reparto entre Pendiente y Vencido sigue siendo correcto
 * aunque la demo se abra dentro de seis meses.
 */
interface Seed {
    days: number;
    /** Índice en WAREHOUSES. */
    wh: number;
    /** Índice en CLIENTS. */
    cl: number;
    /** Índice en CONCEPTS. */
    cp: number;
    qty: number;
    price: number;
    /** Cobrada. Implica status Pagada + payment_status Pagado. */
    paid?: boolean;
    /** Cancelada ante el SAT. Queda fuera de los totales del panel. */
    cancelled?: boolean;
    /** Pago en parcialidades: obliga a forma de pago 99. */
    ppd?: boolean;
    /** Esta factura sustituye a la cancelada (badge "Refacturada"). */
    substitutes?: number;
}

const SEEDS: Seed[] = [
    // --- Almacén 109 ---
    { days: 96, wh: 0, cl: 0, cp: 0, qty: 1240, price: 38.50, paid: true },
    { days: 88, wh: 0, cl: 2, cp: 4, qty: 310, price: 145.00, paid: true },
    { days: 81, wh: 0, cl: 1, cp: 3, qty: 2150, price: 12.80, paid: true, ppd: true },
    { days: 74, wh: 0, cl: 4, cp: 1, qty: 640, price: 62.00, paid: true },
    { days: 66, wh: 0, cl: 0, cp: 5, qty: 880, price: 21.50, cancelled: true },
    { days: 66, wh: 0, cl: 0, cp: 5, qty: 880, price: 21.50, paid: true, substitutes: 4 },
    { days: 59, wh: 0, cl: 3, cp: 2, qty: 46, price: 980.00 },
    { days: 52, wh: 0, cl: 6, cp: 7, qty: 1580, price: 18.40, ppd: true },
    { days: 44, wh: 0, cl: 2, cp: 0, qty: 1310, price: 38.50, paid: true },
    { days: 37, wh: 0, cl: 5, cp: 6, qty: 420, price: 34.00 },
    { days: 24, wh: 0, cl: 1, cp: 4, qty: 285, price: 145.00 },
    { days: 17, wh: 0, cl: 0, cp: 0, qty: 1105, price: 38.50, ppd: true },
    { days: 9,  wh: 0, cl: 7, cp: 3, qty: 1960, price: 12.80 },
    { days: 3,  wh: 0, cl: 4, cp: 1, qty: 705, price: 62.00, ppd: true },

    // --- Monterrey ---
    { days: 92, wh: 1, cl: 3, cp: 1, qty: 520, price: 62.00, paid: true },
    { days: 78, wh: 1, cl: 5, cp: 7, qty: 2240, price: 18.40, paid: true },
    { days: 63, wh: 1, cl: 6, cp: 2, qty: 38, price: 980.00, ppd: true },
    { days: 49, wh: 1, cl: 3, cp: 5, qty: 1420, price: 21.50, paid: true },
    { days: 35, wh: 1, cl: 7, cp: 0, qty: 960, price: 38.50 },
    { days: 21, wh: 1, cl: 5, cp: 4, qty: 340, price: 145.00, ppd: true },
    { days: 12, wh: 1, cl: 6, cp: 6, qty: 610, price: 34.00 },
    { days: 5,  wh: 1, cl: 3, cp: 3, qty: 2480, price: 12.80 },

    // --- Almacén 110 ---
    { days: 85, wh: 2, cl: 7, cp: 4, qty: 260, price: 145.00, paid: true },
    { days: 70, wh: 2, cl: 2, cp: 6, qty: 380, price: 34.00, cancelled: true },
    { days: 56, wh: 2, cl: 4, cp: 2, qty: 29, price: 980.00, paid: true },
    { days: 41, wh: 2, cl: 1, cp: 5, qty: 1180, price: 21.50, ppd: true },
    { days: 28, wh: 2, cl: 7, cp: 1, qty: 480, price: 62.00 },
    { days: 15, wh: 2, cl: 2, cp: 7, qty: 1340, price: 18.40, ppd: true },
    { days: 6,  wh: 2, cl: 4, cp: 0, qty: 820, price: 38.50 },
    { days: 1,  wh: 2, cl: 1, cp: 3, qty: 1750, price: 12.80 },

    // --- Cartera vencida: una por cada tramo de antigüedad de cuentas por
    // cobrar (61-90, 91-120 y +120 días), repartida entre los tres almacenes.
    { days: 102, wh: 0, cl: 3, cp: 0, qty: 740, price: 38.50 },
    { days: 118, wh: 1, cl: 6, cp: 4, qty: 195, price: 145.00, ppd: true },
    { days: 133, wh: 0, cl: 5, cp: 2, qty: 33, price: 980.00 },
    { days: 147, wh: 2, cl: 1, cp: 1, qty: 415, price: 62.00 },
    { days: 172, wh: 1, cl: 3, cp: 6, qty: 290, price: 34.00, ppd: true },
    { days: 205, wh: 0, cl: 7, cp: 5, qty: 640, price: 21.50 },
    { days: 248, wh: 2, cl: 5, cp: 7, qty: 880, price: 18.40 }
];

/**
 * Servicios de almacén ya prestados que todavía no se han facturado. Son los
 * que busca el paso "Servicio a facturar" de la pantalla de alta.
 */
const PENDING_SERVICES: Array<{ days: number; wh: number; cl: number; cp: number; qty: number; price: number }> = [
    { days: 2, wh: 0, cl: 0, cp: 0, qty: 960, price: 38.50 },
    { days: 3, wh: 0, cl: 2, cp: 4, qty: 215, price: 145.00 },
    { days: 4, wh: 1, cl: 3, cp: 1, qty: 540, price: 62.00 },
    { days: 5, wh: 2, cl: 1, cp: 3, qty: 1620, price: 12.80 },
    { days: 7, wh: 0, cl: 5, cp: 2, qty: 27, price: 980.00 },
    { days: 8, wh: 1, cl: 6, cp: 7, qty: 1180, price: 18.40 },
    { days: 9, wh: 2, cl: 4, cp: 5, qty: 740, price: 21.50 },
    { days: 11, wh: 0, cl: 7, cp: 6, qty: 395, price: 34.00 },
    { days: 12, wh: 1, cl: 0, cp: 3, qty: 2040, price: 12.80 },
    { days: 14, wh: 2, cl: 3, cp: 0, qty: 1120, price: 38.50 },
    { days: 16, wh: 0, cl: 1, cp: 1, qty: 620, price: 62.00 },
    { days: 18, wh: 1, cl: 5, cp: 4, qty: 305, price: 145.00 }
];

/**
 * Facturas de almacén de prueba.
 *
 * El backend todavía no distingue las facturas de almacén de las de transporte
 * —no hay ni tabla de almacenes— así que `invoice/list` no puede devolver este
 * subconjunto. Mientras tanto la pestaña de almacén se alimenta de aquí.
 *
 * Cuando exista el endpoint, `list()` pasa a ser una llamada HTTP y el panel no
 * cambia: la firma y el shape de las filas ya son los del listado real.
 */
@Injectable({ providedIn: 'root' })
export class WarehouseInvoiceService {
    private rows: WarehouseInvoiceRow[] | null = null;
    private pending: WarehousePendingService[] | null = null;
    /** Insumos dados de alta desde la pantalla de facturación, que todavía no
     *  están en el catálogo. Sin esto, una factura suya quedaría fuera del
     *  desglose por concepto y los KPI's dejarían de cuadrar con el listado. */
    private extraConcepts: Array<{ name: string; productServiceKey: string; unitKey: string }> = [];

    /**
     * Listado filtrado. Los filtros se aplican aquí con la misma semántica que
     * InvoiceController@index (búsqueda por folio/cliente/RFC, igualdad en el
     * resto) para que la pantalla se comporte igual que contra el backend.
     */
    list(filters: WarehouseInvoiceFilters = {}): Observable<WarehouseInvoiceRow[]> {
        const search = (filters.search || '').trim().toLowerCase();

        const rows = this.all().filter(row => {
            if (filters.warehouse && row.warehouse !== filters.warehouse) return false;
            if (filters.status && row.status !== filters.status) return false;
            if (filters.payment_status && row.payment_status !== filters.payment_status) return false;
            if (filters.payment_method && row.payment_method !== filters.payment_method) return false;
            if (filters.issued_at && row.issued_at !== filters.issued_at) return false;
            if (search) {
                const haystack = `${row.folio} ${row.client_name} ${row.client_rfc}`.toLowerCase();
                if (!haystack.includes(search)) return false;
            }
            return true;
        });

        // Un tick de retraso: el panel muestra el spinner y lo oculta al
        // responder; sin asincronía el spinner se abre y cierra en el mismo
        // ciclo de detección de cambios y ngx-spinner se queda colgado.
        return of(rows).pipe(delay(120));
    }

    /**
     * Antigüedad de saldos por cliente, con la misma definición que
     * `invoice/accounts-receivable`: sólo facturas vivas sin cobrar, y el tramo
     * lo marca el vencimiento (emisión + días de crédito), no la emisión.
     *
     * Se calcula desde las mismas facturas que alimentan el listado para que
     * las dos pestañas no puedan contradecirse.
     */
    accountsReceivable(creditDays: number = CREDIT_DAYS, warehouse?: string): Observable<WarehouseReceivable> {
        const pending = this.all().filter(row =>
            row.status !== 'Cancelada'
            && (row.payment_status === 'Pendiente' || row.payment_status === 'Vencido')
            && (!warehouse || row.warehouse === warehouse));

        const totals = this.emptyBuckets();
        const byClient = new Map<number, WarehouseReceivableRow>();

        for (const invoice of pending) {
            const bucket = this.bucketOf(invoice.issued_at, creditDays);

            let client = byClient.get(invoice.client_id);
            if (!client) {
                client = { client_id: invoice.client_id, client_name: invoice.client_name, ...this.emptyBuckets() };
                byClient.set(invoice.client_id, client);
            }

            client[bucket] += invoice.total;
            client.total += invoice.total;
            totals[bucket] += invoice.total;
            totals.total += invoice.total;
        }

        // Redondeo al final: sumar decimales de a poco arrastra error y los
        // totales dejarían de cuadrar con la suma de las columnas.
        const rows = [...byClient.values()]
            .map(row => ({ ...row, ...this.roundBuckets(row) }))
            .sort((a, b) => a.client_name.localeCompare(b.client_name, 'es'));

        return of({ totals: { ...totals, ...this.roundBuckets(totals) }, rows }).pipe(delay(120));
    }

    /**
     * Facturado por concepto y por mes, sin canceladas (un CFDI cancelado no es
     * ingreso, igual que en el listado y en la cartera).
     *
     * Todos los conceptos del catálogo aparecen aunque no tengan facturas, y
     * los meses van completos de punta a punta: un hueco dejaría dos barras
     * contiguas en el gráfico que en realidad están separadas por un mes sin
     * facturación.
     */
    chargedSummary(warehouse?: string): Observable<WarehouseChargedSummary> {
        const invoices = this.all().filter(row =>
            row.status !== 'Cancelada' && (!warehouse || row.warehouse === warehouse));

        const byConcept = new Map<string, number>();
        const byMonth = new Map<string, Map<string, number>>();

        for (const invoice of invoices) {
            byConcept.set(invoice.concept, (byConcept.get(invoice.concept) ?? 0) + invoice.total);

            const month = invoice.issued_at.slice(0, 7);
            let cells = byMonth.get(month);
            if (!cells) {
                cells = new Map<string, number>();
                byMonth.set(month, cells);
            }
            cells.set(invoice.concept, (cells.get(invoice.concept) ?? 0) + invoice.total);
        }

        const catalog = this.conceptCatalog;
        const conceptTotals = (cells: Map<string, number>): WarehouseChargedConcept[] =>
            catalog.map(concept => ({
                concept: concept.name,
                charged: this.round2(cells.get(concept.name) ?? 0)
            }));

        const months = this.monthRange([...byMonth.keys()]).map(month => {
            const cells = conceptTotals(byMonth.get(month) ?? new Map<string, number>());
            return {
                month,
                label: this.monthLabel(month),
                charged: this.round2(cells.reduce((acc, cell) => acc + cell.charged, 0)),
                byConcept: cells
            };
        });

        return of({
            total: this.round2(invoices.reduce((acc, invoice) => acc + invoice.total, 0)),
            byConcept: conceptTotals(byConcept),
            byMonth: months
        }).pipe(delay(120));
    }

    /** Catálogo de servicios de almacén facturables, incluidos los dados de
     *  alta en esta sesión. */
    get concepts(): string[] {
        return this.conceptCatalog.map(concept => concept.name);
    }

    /** Clientes con sus datos fiscales, para el paso 1 de la pantalla de alta. */
    get clients(): WarehouseClient[] {
        return CLIENTS;
    }

    /** Clave SAT y unidad con las que se factura cada servicio de almacén. */
    get conceptCatalog(): Array<{ name: string; productServiceKey: string; unitKey: string }> {
        return [...CONCEPTS, ...this.extraConcepts].map(concept => ({ ...concept }));
    }

    /**
     * Servicios prestados y sin facturar. `search` filtra por folio o cliente,
     * igual que el buscador de la pantalla.
     */
    pendingServices(search?: string): Observable<WarehousePendingService[]> {
        if (!this.pending) {
            this.pending = PENDING_SERVICES.map((seed, index) => {
                const client = CLIENTS[seed.cl];
                const concept = CONCEPTS[seed.cp];
                return {
                    id: 5001 + index,
                    folio: `SA-${2040 + index}`,
                    clientId: client.id,
                    clientName: client.name,
                    warehouse: WAREHOUSES[seed.wh],
                    concept: concept.name,
                    productServiceKey: concept.productServiceKey,
                    unitKey: concept.unitKey,
                    quantity: seed.qty,
                    unitPrice: seed.price,
                    servedAt: this.isoDaysAgo(seed.days)
                };
            });
        }

        const needle = (search || '').trim().toLowerCase();
        const rows = needle
            ? this.pending.filter(service =>
                `${service.folio} ${service.clientName} ${service.concept}`.toLowerCase().includes(needle))
            : this.pending;

        return of(rows).pipe(delay(120));
    }

    /**
     * Registra una factura nueva y la devuelve ya construida.
     *
     * Vive sólo en memoria: al recargar la página vuelve el juego de prueba
     * original. Lo importante es que la factura pasa por las mismas reglas que
     * las de la semilla (importes, estados derivados, forma de pago 99 en PPD),
     * así que aparece en el listado, en los KPI's y en la cartera sin romper
     * ninguna de sus validaciones.
     */
    add(draft: WarehouseInvoiceDraft): Observable<WarehouseInvoiceRow> {
        const rows = this.all();
        const client = CLIENTS.find(c => c.id === draft.clientId);
        if (!client) throw new Error(`Cliente desconocido: ${draft.clientId}`);

        // Un insumo dado de alta en la pantalla entra al catálogo: si no, su
        // importe se contaría en el total pero no en el desglose por concepto.
        let concept = this.conceptCatalog.find(c => c.name === draft.concept);
        if (!concept) {
            concept = {
                name: draft.concept,
                productServiceKey: draft.productServiceKey || '78131800',
                unitKey: 'E48'
            };
            this.extraConcepts.push(concept);
        }

        const subtotal = this.round2(draft.quantity * draft.unitPrice);
        const iva = this.round2(subtotal * IVA_RATE);
        const issuedAt = this.isoDaysAgo(0);
        const id = rows.reduce((max, row) => Math.max(max, row.id), 9000) + 1;
        const folioNumber = rows.reduce(
            (max, row) => Math.max(max, Number(row.folio.replace('ALM-', '')) || 0), 1000) + 1;

        const invoice: WarehouseInvoiceRow = {
            id,
            folio: `ALM-${folioNumber}`,
            total: this.round2(subtotal + iva),
            issued_at: issuedAt,
            // Recién timbrada: vigente y sin cobrar, y hoy no puede estar vencida.
            status: 'Vigente',
            payment_status: 'Pendiente',
            payment_method: draft.paymentMethod,
            client_id: client.id,
            client_name: client.name,
            client_rfc: client.rfc,
            is_refactura: false,
            was_refacturada: false,
            serie: 'ALM',
            uuid_sat: this.uuidFor(id),
            due_at: this.isoDaysAgo(-CREDIT_DAYS),
            subtotal,
            iva,
            // Misma regla del SAT que en la semilla, por si llegara un PPD con
            // otra forma de pago desde el formulario.
            payment_form_key: draft.paymentMethod === 'PPD' ? '99' : draft.paymentFormKey,
            product_service_key: draft.productServiceKey || concept.productServiceKey,
            unit_key: concept.unitKey,
            quantity: draft.quantity,
            unit_price: draft.unitPrice,
            warehouse: draft.warehouse,
            concept: draft.concept
        };

        rows.unshift(invoice);
        return of(invoice).pipe(delay(160));
    }

    /** Se genera una sola vez: las filas se comparan por referencia en el grid. */
    private all(): WarehouseInvoiceRow[] {
        if (!this.rows) this.rows = this.build();
        return this.rows;
    }

    private build(): WarehouseInvoiceRow[] {
        const rows = SEEDS.map((seed, index) => this.buildRow(seed, index));

        // La refacturación son dos filas que se apuntan entre sí: la cancelada
        // queda marcada como sustituida y la nueva como sustituta.
        SEEDS.forEach((seed, index) => {
            if (seed.substitutes === undefined) return;
            const original = rows[seed.substitutes];
            if (!original) return;
            original.was_refacturada = true;
            rows[index].is_refactura = true;
        });

        // Más reciente primero, igual que el orderByDesc('issued_at') del back.
        return rows.sort((a, b) => b.issued_at.localeCompare(a.issued_at) || b.id - a.id);
    }

    private buildRow(seed: Seed, index: number): WarehouseInvoiceRow {
        const client = CLIENTS[seed.cl];
        const concept = CONCEPTS[seed.cp];
        const id = 9001 + index;

        const subtotal = this.round2(seed.qty * seed.price);
        const iva = this.round2(subtotal * IVA_RATE);
        const total = this.round2(subtotal + iva);

        const issuedAt = this.isoDaysAgo(seed.days);
        const dueAt = this.isoDaysAgo(seed.days - CREDIT_DAYS);

        // Estado y estado de pago se derivan, no se escriben en la semilla: así
        // no puede aparecer una cancelada que además figure como pagada, ni una
        // pendiente cuyo crédito ya venció.
        const status: WarehouseInvoiceRow['status'] = seed.cancelled
            ? 'Cancelada'
            : (seed.paid ? 'Pagada' : 'Vigente');

        let paymentStatus: WarehouseInvoiceRow['payment_status'];
        if (seed.paid) {
            paymentStatus = 'Pagado';
        } else if (!seed.cancelled && seed.days > CREDIT_DAYS) {
            paymentStatus = 'Vencido';
        } else {
            paymentStatus = 'Pendiente';
        }

        const paymentMethod: WarehouseInvoiceRow['payment_method'] = seed.ppd ? 'PPD' : 'PUE';

        return {
            id,
            folio: `ALM-${1000 + index}`,
            total,
            issued_at: issuedAt,
            status,
            payment_status: paymentStatus,
            payment_method: paymentMethod,
            client_id: client.id,
            client_name: client.name,
            client_rfc: client.rfc,
            is_refactura: false,
            was_refacturada: false,

            serie: 'ALM',
            uuid_sat: this.uuidFor(id),
            due_at: dueAt,
            subtotal,
            iva,
            // Regla del SAT: en PPD la forma de pago tiene que ser 99 (Por
            // definir); en PUE se declara la real, aquí transferencia.
            payment_form_key: paymentMethod === 'PPD' ? '99' : '03',
            product_service_key: concept.productServiceKey,
            unit_key: concept.unitKey,
            quantity: seed.qty,
            unit_price: seed.price,

            warehouse: WAREHOUSES[seed.wh],
            concept: concept.name
        };
    }

    private round2(value: number): number {
        return Math.round(value * 100) / 100;
    }

    private emptyBuckets(): WarehouseReceivableBuckets {
        return { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d91_120: 0, d120_plus: 0, total: 0 };
    }

    private roundBuckets(buckets: WarehouseReceivableBuckets): WarehouseReceivableBuckets {
        return {
            current: this.round2(buckets.current),
            d1_30: this.round2(buckets.d1_30),
            d31_60: this.round2(buckets.d31_60),
            d61_90: this.round2(buckets.d61_90),
            d91_120: this.round2(buckets.d91_120),
            d120_plus: this.round2(buckets.d120_plus),
            total: this.round2(buckets.total)
        };
    }

    /** Tramo de antigüedad según los días transcurridos desde el vencimiento. */
    private bucketOf(issuedAt: string, creditDays: number): keyof Omit<WarehouseReceivableBuckets, 'total'> {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(`${issuedAt}T00:00:00`);
        due.setDate(due.getDate() + creditDays);
        const overdue = Math.round((today.getTime() - due.getTime()) / 86400000);

        if (overdue <= 0) return 'current';
        if (overdue <= 30) return 'd1_30';
        if (overdue <= 60) return 'd31_60';
        if (overdue <= 90) return 'd61_90';
        if (overdue <= 120) return 'd91_120';
        return 'd120_plus';
    }

    /** Todos los meses entre el primero y el último, sin saltarse los vacíos. */
    private monthRange(months: string[]): string[] {
        if (!months.length) return [];

        const sorted = [...months].sort();
        const last = sorted[sorted.length - 1];
        const [year, month] = sorted[0].split('-').map(Number);
        const cursor = new Date(year, month - 1, 1);
        const range: string[] = [];

        // El tope evita que un dato corrupto convierta esto en un bucle infinito.
        for (let guard = 0; guard < 240; guard++) {
            const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
            range.push(key);
            if (key === last) break;
            cursor.setMonth(cursor.getMonth() + 1);
        }
        return range;
    }

    /** `2026-07` → `Jul`. */
    private monthLabel(month: string): string {
        const [year, index] = month.split('-').map(Number);
        const label = new Date(year, index - 1, 1)
            .toLocaleDateString('es-MX', { month: 'short' })
            .replace('.', '');
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    /** `aaaa-mm-dd` de hace `daysAgo` días (negativo = futuro). */
    private isoDaysAgo(daysAgo: number): string {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() - daysAgo);
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${date.getFullYear()}-${mm}-${dd}`;
    }

    /**
     * UUID con formato de folio fiscal válido (8-4-4-4-12, versión 4 y variante
     * RFC 4122). Derivado del id, no aleatorio: recargar la pantalla no debe
     * cambiar el folio fiscal de una factura.
     */
    private uuidFor(id: number): string {
        // Avalancha de bits (lowbias32): sin ella dos salidas del mismo id
        // comparten los dígitos altos y los UUID salen con trozos repetidos.
        const mix = (salt: number) => {
            let x = (Math.imul(id, 0x9E3779B1) + Math.imul(salt, 0x85EBCA77)) >>> 0;
            x = Math.imul(x ^ (x >>> 16), 0x7FEB352D) >>> 0;
            x = Math.imul(x ^ (x >>> 15), 0x846CA68B) >>> 0;
            x = (x ^ (x >>> 16)) >>> 0;
            return x.toString(16).toUpperCase().padStart(8, '0');
        };
        const a = mix(1);
        const b = mix(2);
        const c = mix(3);
        const d = mix(4);
        return `${a}-${b.slice(0, 4)}-4${b.slice(4, 7)}-A${c.slice(0, 3)}-${c.slice(3)}${d.slice(0, 7)}`;
    }
}
